import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  BLOG_LANGUAGES,
  blogPostsIn,
  blogTranslations,
  getBlogPost,
  blogPlainText,
} from '@affily/shared/blog';
import { Seo } from '../components/Seo.jsx';
import { Inline } from './Help.jsx';

/**
 * The blog: longer pieces than a help answer. The posts live in
 * shared/blog.js, so the prerender and the sitemap list exactly these pages.
 */

const published = (post) =>
  new Date(`${post.publishedAt}T12:00:00Z`).toLocaleDateString(BLOG_LANGUAGES[post.lang].locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

const postJsonLd = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.summary,
  inLanguage: post.lang,
  datePublished: post.publishedAt,
  publisher: { '@type': 'Organization', name: 'Affily' },
});

function Body({ post }) {
  return (
    <div className="space-y-4 text-ink-muted">
      {post.body.map((block, i) => {
        if (typeof block === 'string') {
          return (
            <p key={i}>
              <Inline text={block} />
            </p>
          );
        }
        if (block.heading) {
          return (
            <h2 key={i} className="pt-6 text-2xl font-semibold tracking-tight text-ink">
              {block.heading}
            </h2>
          );
        }
        return (
          <ul key={i} className="list-disc space-y-1.5 pl-5">
            {block.list.map((item, j) => (
              <li key={j}>
                <Inline text={item} />
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
}

/** "Also in: Română · Ελληνικά · Български", linking the same post in each. */
function AlsoIn({ post, className = '' }) {
  const others = blogTranslations(post);
  if (!others.length) return null;
  return (
    <p className={`text-sm text-ink-muted ${className}`}>
      {BLOG_LANGUAGES[post.lang].alsoIn}:{' '}
      {others.map((t, i) => (
        <span key={t.slug}>
          {i > 0 && ' · '}
          <Link to={`/blog/${t.slug}`} lang={t.lang} className="text-accent hover:underline">
            {BLOG_LANGUAGES[t.lang].name}
          </Link>
        </span>
      ))}
    </p>
  );
}

export function BlogIndex() {
  const [params, setParams] = useSearchParams();
  const lang = BLOG_LANGUAGES[params.get('lang')] ? params.get('lang') : 'en';
  const posts = blogPostsIn(lang);
  // Only the languages something is written in.
  const languages = Object.keys(BLOG_LANGUAGES).filter((l) => blogPostsIn(l).length);

  return (
    <>
      <Seo
        title="Blog"
        description="How creator programs work on Affily: store connections, tracking, and the brands it’s built for."
      />
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <h1 className="text-4xl font-semibold tracking-tight">Blog</h1>
        {languages.length > 1 && (
          <div className="mt-6 flex flex-wrap gap-1.5" role="group" aria-label="Language">
            {languages.map((l) => (
              <button
                key={l}
                type="button"
                lang={l}
                onClick={() => setParams(l === 'en' ? {} : { lang: l }, { replace: true })}
                aria-pressed={l === lang}
                className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  l === lang
                    ? 'border-accent bg-accent text-white'
                    : 'border-rule-strong bg-paper-raised text-ink-muted hover:text-ink'
                }`}
              >
                {BLOG_LANGUAGES[l].name}
              </button>
            ))}
          </div>
        )}
        <ul className="mt-10 space-y-8" lang={lang}>
          {posts.map((post) => (
            <li key={post.slug}>
              <p className="text-sm text-ink-faint">{published(post)}</p>
              <Link to={`/blog/${post.slug}`} className="mt-1 block text-xl font-semibold tracking-tight hover:text-accent">
                {post.title}
              </Link>
              <p className="mt-2 text-ink-muted">{post.summary}</p>
              <AlsoIn post={post} className="mt-2" />
            </li>
          ))}
        </ul>
      </article>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = getBlogPost(slug);

  if (!post) {
    return (
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6">
        <Seo title="Not found" noindex />
        <h1 className="text-3xl font-semibold tracking-tight">No such post</h1>
        <p className="mt-4 text-ink-muted">
          It may have moved. <Link to="/blog" className="text-accent hover:underline">All posts</Link>
        </p>
      </article>
    );
  }

  const words = BLOG_LANGUAGES[post.lang];
  const allPosts = post.lang === 'en' ? '/blog' : `/blog?lang=${post.lang}`;

  return (
    <>
      <Seo
        title={post.title}
        description={blogPlainText(post).slice(0, 160)}
        jsonLd={postJsonLd(post)}
        lang={post.lang}
        alternates={[post, ...blogTranslations(post)].map((t) => ({ lang: t.lang, path: `/blog/${t.slug}` }))}
      />
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6" lang={post.lang}>
        <p className="text-sm text-ink-muted">
          <Link to={allPosts} className="hover:text-ink">
            Blog
          </Link>{' '}
          · {published(post)}
        </p>
        <h1 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">{post.title}</h1>
        <p className="mt-4 text-xl text-ink-muted">{post.summary}</p>
        <AlsoIn post={post} className="mt-3" />
        <div className="mt-8 text-lg">
          <Body post={post} />
        </div>
        <p className="mt-12 text-sm text-ink-muted">
          <Link to={allPosts} className="text-accent hover:underline">{words.allPosts}</Link> ·{' '}
          <Link to="/help" className="text-accent hover:underline">{words.help}</Link>
        </p>
      </article>
    </>
  );
}
